import { Slider, SliderProps, SliderThumb, SliderTrack, SliderOutput, Label } from "react-aria-components";
import { useCallback } from "react";

type SliderValue = number | number[];

interface SliderFieldProps<T extends SliderValue = number> extends Omit<SliderProps, "children" | "value" | "onChange"> {
	value: T;
	onChange: (value: T) => void;
	label?: string;
	helperText?: string;
	formatOutput?: (value: T) => string;
}

export function SliderField<T extends SliderValue>({ value, onChange, label, helperText, formatOutput, ...rest }: SliderFieldProps<T>) {
	const handleChange = useCallback((v: number | number[]) => onChange(v as T), [onChange]);

	let outputLabel: string;
	if (formatOutput) {
		outputLabel = formatOutput(value);
	} else if (Array.isArray(value)) {
		outputLabel = value.join(" – ");
	} else {
		outputLabel = String(value);
	}

	return (
		<div className="flex flex-col gap-2">
			{label && <Label className="text-base font-medium text-Text-secondary">{label}</Label>}
			<Slider {...rest} value={value} onChange={handleChange} className="flex">
				<SliderTrack className="relative w-full h-5 mr-[16px]">
					{({ state }) => (
						<>
							<div className="absolute left-0 top-1/2 h-1.5 w-full -translate-y-1/2 rounded-full bg-Surface-action-neutral-hover" />
							<div
								className="absolute left-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-navy-800"
								style={{ width: `${state.getThumbPercent(0) * 100}%` }}
							/>
							<SliderThumb
								className="h-7.5 w-7.5 top-[25px] -translate-y-1/2 rounded-full bg-navy-800
                             shadow-md transition
                             focus-visible:outline-none focus-visible:ring-4
                             "
							/>
						</>
					)}
				</SliderTrack>

				<SliderOutput
					aria-label="Current value"
					className="shrink-0 flex items-center justify-center w-[64px] h-[55px]
               rounded-xl border border-[#DEDEE3]
               text-base leading-none font-light text-Text-primary"
				>
					{outputLabel}
				</SliderOutput>
			</Slider>

			{helperText && <p className="text-base font-light leading-none text-[#666B88]">{helperText}</p>}
		</div>
	);
}
